'use client';

import { ImageOff } from 'lucide-react';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Breadcrumb from '@/components/ui/Breadcrumb';
import { buildPublicUrl } from '@/lib/r2-urls';
import { getBlurDataURL } from '@/lib/shimmer';
import safeURL from '@/lib/url';

const toSrcSetEntries = (value) => {
    if (!value) return [];
    const list = Array.isArray(value) ? value : String(value).split(',');
    return list
        .map((entry) => {
            if (!entry) return null;
            const trimmed = String(entry).trim();
            if (!trimmed) return null;
            const lastSpace = trimmed.lastIndexOf(' ');
            if (lastSpace === -1) {
                return { url: trimmed, descriptor: '' };
            }
            return {
                url: trimmed.slice(0, lastSpace),
                descriptor: trimmed.slice(lastSpace + 1),
            };
        })
        .filter((item) => item?.url);
};

const formatSrcSet = (value, formatter = (url) => url) => {
    const entries = toSrcSetEntries(value);
    if (entries.length === 0) return undefined;
    return entries
        .map(({ url, descriptor }) => {
            const formattedUrl = formatter(url);
            return descriptor ? `${formattedUrl} ${descriptor}` : formattedUrl;
        })
        .join(', ');
};

// Responsive candidates arrive as CDN keys (e.g.
// "/product/gn-solids/gn-solids-background-small.webp"), not as absolute URLs.
// Resolve them through the same buildPublicUrl() used for the base src —
// otherwise every srcSet candidate is requested from the app origin instead of
// the configured CDN, and the CDN is bypassed entirely for that image.
const toCdnUrl = (url) => {
    try {
        return safeURL(buildPublicUrl(url) || url);
    } catch (_e) {
        return safeURL(url);
    }
};

const DownArrow = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="50"
        height="50"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#ff6900"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="animate-bounce"
        aria-hidden="true"
    >
        <title>Scroll down</title>
        <path d="M6 9l6 6 6-6" />
    </svg>
);

export const COMPACT_PAGE_HEADER_HEIGHT =
    'min-h-[260px] sm:min-h-[290px] lg:min-h-[320px]';
const PageHeader = ({
    title,
    description,
    breadcrumbs = [],
    backLabel,
    backgroundImage = safeURL('/images/20130508_092219.webp'),
    backgroundAlt = 'Professional trench protection equipment at construction site',
    children,
    className = '',
    height = 'min-h-[45vh] md:min-h-[40vh] lg:min-h-[380px]',
    showProductImage = false,
    productImage = null,
    productImageAlt = '',
    onProductImageClick = null,
    primaryAction = null,
    secondaryAction = null,
    loading = false,
    showScrollButton = false,
    contentContainerClassName = 'w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 relative z-20',
    // server-provided optional precomputed props
    bgSrcSet: bgSrcSetProp,
    bgBlurDataURL: bgBlurDataURLProp,
    prodSrcSet: prodSrcSetProp,
    prodBlurDataURL: prodBlurDataURLProp,
    isAdmin = false,
}) => {
    // instrumentation helper for consistent JSON logs with contextual data
    const logEvent = useCallback((payload) => {
        try {
            const nav =
                typeof navigator !== 'undefined'
                    ? {
                          ua: navigator.userAgent,
                          connection:
                              navigator.connection?.effectiveType || null,
                      }
                    : {};
            const vis =
                typeof document !== 'undefined'
                    ? document.visibilityState
                    : null;
            const base = {
                ts: new Date().toISOString(),
                perfNow:
                    typeof performance !== 'undefined'
                        ? performance.now()
                        : null,
            };
            console.warn(
                JSON.stringify({
                    ...base,
                    ...payload,
                    navigator: nav,
                    visibility: vis,
                }),
            );
        } catch (_e) {
            /* swallow */
        }
    }, []);
    // Accept precomputed responsive props from server: bgSrcSet, bgBlurDataURL
    // Fallback: client-side shimmer
    const bgSrcSet =
        typeof bgSrcSetProp !== 'undefined' ? bgSrcSetProp : undefined;
    const bgBlurDataURL =
        typeof bgBlurDataURLProp !== 'undefined'
            ? bgBlurDataURLProp
            : undefined;
    const prodSrcSet =
        typeof prodSrcSetProp !== 'undefined' ? prodSrcSetProp : undefined;
    const prodBlurDataURL =
        typeof prodBlurDataURLProp !== 'undefined'
            ? prodBlurDataURLProp
            : undefined;

    // Fallback background image, used only if the real background errors out
    const fallbackBgImage = safeURL('/images/20130508_092219.webp');

    const sectionRef = useRef(null);

    // Local state for image loading/error — drives the blur-placeholder →
    // real-image transition. AppImage/next-image handle preloading, decoding,
    // and CDN srcSet natively. This component only tracks whether the image
    // has reported itself loaded (via onLoad/onError callbacks) so it can
    // fade the blur placeholder out.
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgError, setImgError] = useState(false);
    const [prodImgLoaded, setProdImgLoaded] = useState(false);
    const [prodImgError, setProdImgError] = useState(false);

    // buildPublicUrl() is a pure function of NEXT_PUBLIC_R2_URL (inlined at
    // build time), so it resolves identically on the server and the client —
    // no need to stage this through an effect just to "upgrade" the src post-hydration.
    const resolvedBgSrc = useMemo(() => {
        if (!backgroundImage) return backgroundImage;
        try {
            return buildPublicUrl(backgroundImage) || backgroundImage;
        } catch (_e) {
            return backgroundImage;
        }
    }, [backgroundImage]);

    const resolvedProdSrc = useMemo(() => {
        if (!productImage) return productImage;
        try {
            return buildPublicUrl(productImage) || productImage;
        } catch (_e) {
            return productImage;
        }
    }, [productImage]);

    // Reset loaded/error state when the source changes (e.g. client-side
    // navigation between pages/products reusing this component). The
    // dependency is intentionally used only as a change-trigger, not
    // referenced in the body.
    // biome-ignore lint/correctness/useExhaustiveDependencies: resolvedBgSrc is a deliberate reset trigger
    useEffect(() => {
        setImgLoaded(false);
        setImgError(false);
    }, [resolvedBgSrc]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: resolvedProdSrc is a deliberate reset trigger
    useEffect(() => {
        setProdImgLoaded(false);
        setProdImgError(false);
    }, [resolvedProdSrc]);

    // Recovery fallback for SSG/SSR hydration: when the page is statically
    // generated (like /products), the <img> element ships in the initial HTML
    // and the browser starts loading it immediately — often finishing before
    // React finishes hydrating and attaches the synthetic `onLoad` handler.
    // In that case React never fires onLoad, so `imgLoaded` stays false,
    // and the blur placeholder overlay (z-20) stays on top of the real image
    // forever.  To recover, we inspect the native DOM element directly.
    // biome-ignore lint/correctness/useExhaustiveDependencies: resolvedBgSrc is a deliberate reset trigger
    useEffect(() => {
        if (imgLoaded) return;
        const timer = setTimeout(() => {
            try {
                const imgEl = sectionRef.current?.querySelector('img');
                if (imgEl?.complete && imgEl.naturalWidth > 0) {
                    setImgLoaded(true);
                }
            } catch (_e) {
                // DOM query failed — keep waiting for the real onLoad
            }
        }, 600);
        return () => clearTimeout(timer);
    }, [imgLoaded, resolvedBgSrc]);

    // Compute final background src and ensure we don't pass an empty string to Next/Image
    const finalBgSrc = imgError
        ? fallbackBgImage
        : resolvedBgSrc || fallbackBgImage;
    const safeFinalBgSrc = finalBgSrc ? safeURL(finalBgSrc) : null;

    return (
        <section
            ref={sectionRef}
            className={`relative overflow-hidden ${height} flex items-center ${className}`}
            aria-busy={loading ? 'true' : 'false'}
        >
            {/* Live region for assistive tech to announce header loading state */}
            <output className="sr-only" aria-live="polite">
                {loading ? 'Loading header' : ''}
            </output>
            <div className="absolute inset-0">
                {/* subtle, low-contrast loading layer to avoid jarring UI shifts - use a light
                    translucent layer so the blur placeholder remains visible instead of black */}
                {!imgLoaded && (
                    <div
                        className={`w-full h-full ${loading ? 'bg-white/10' : 'bg-white/5'} backdrop-blur-sm absolute inset-0 z-10 pointer-events-none transition-opacity duration-700`}
                        aria-hidden="true"
                    />
                )}

                {/* Blurred placeholder image: this ensures a visible shimmer even when the
                    real image fails silently (network aborts / 404). It sits above the
                    Next/Image element until the image reports itself as loaded. */}
                {!imgLoaded && (
                    <div
                        className="absolute inset-0 z-20 pointer-events-none bg-center bg-cover"
                        style={{
                            backgroundImage: `url(${safeURL(bgBlurDataURL || getBlurDataURL(16, 12))})`,
                        }}
                        aria-hidden="true"
                    />
                )}

                {/* Background image - ensure we only render the Image when we have a valid src */}
                {safeFinalBgSrc && (
                    <AppImage
                        src={safeFinalBgSrc}
                        alt={backgroundAlt}
                        fill
                        className={`object-cover transition-[filter,transform] duration-1000 bg-transparent relative z-0 page-header-bg-fadein ${
                            imgLoaded ? 'blur-0 scale-100' : 'blur-sm scale-105'
                        }`}
                        priority
                        fetchPriority="high"
                        sizes="100vw"
                        srcSet={
                            !imgError && bgSrcSet
                                ? formatSrcSet(bgSrcSet, toCdnUrl)
                                : undefined
                        }
                        onLoad={() => setImgLoaded(true)}
                        onError={() => {
                            logEvent({
                                level: 'warn',
                                service: 'page_header',
                                event: 'background_load_failed',
                                backgroundImage: resolvedBgSrc || null,
                            });
                            setImgError(true);
                            setImgLoaded(true);
                        }}
                        placeholder="blur"
                        blurDataURL={bgBlurDataURL || getBlurDataURL(16, 12)}
                    />
                )}

                {imgError && (
                    // Render a straightforward img as a last-resort fallback (avoids Next/Image optimization pipeline when it fails)
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        // final last-resort fallback uses the local hero image to avoid HTML 404 responses
                        src={fallbackBgImage}
                        alt={backgroundAlt}
                        className={`object-cover transition-[filter,transform] duration-700 bg-transparent absolute inset-0 w-full h-full page-header-bg-fadein ${imgLoaded ? 'blur-0 scale-100' : 'blur-sm scale-105'}`}
                        onLoad={() => setImgLoaded(true)}
                        onError={() => {
                            logEvent({
                                level: 'warn',
                                service: 'page_header',
                                event: 'background_fallback_failed',
                            });
                        }}
                    />
                )}

                {/* Dark overlay for improved contrast: subtle full overlay + bottom-heavy gradient */}
                {!loading && (
                    <div className="absolute inset-0 pointer-events-none">
                        {/* Soft overall dim to improve contrast against busy images */}
                        <div className="absolute inset-0 bg-black/20" />
                        {/* Bottom-to-transparent gradient to darken where the title sits */}
                        <div className="absolute inset-0 bg-linear-to-t from-black/60 via-black/30 to-transparent" />
                    </div>
                )}
            </div>

            <div className={contentContainerClassName}>
                <div
                    className={`grid grid-cols-1 ${
                        showProductImage ? 'lg:grid-cols-2' : ''
                    } gap-12 items-center`}
                >
                    {/* Content */}
                    <div className="space-y-3 md:space-y-6 text-white">
                        {/* Breadcrumbs */}
                        {!loading && breadcrumbs.length > 0 && (
                            <Breadcrumb
                                items={breadcrumbs}
                                backLabel={backLabel}
                            />
                        )}

                        {/* Title */}
                        {loading ? (
                            <div
                                className="h-10 w-64 bg-gray-300 rounded mb-4 motion-safe:animate-pulse motion-reduce:animate-none"
                                aria-hidden="true"
                            />
                        ) : (
                            <h1
                                className={`text-4xl sm:text-6xl font-bold text-white leading-tight ${isAdmin ? 'line-clamp-2' : ''}`}
                            >
                                {title}
                            </h1>
                        )}

                        {/* Description */}
                        {loading ? (
                            <div
                                className="h-6 w-96 bg-gray-300 rounded mb-4 motion-safe:animate-pulse motion-reduce:animate-none"
                                aria-hidden="true"
                            />
                        ) : (
                            description && (
                                <p className="text-md sm:text-xl text-gray-100 leading-relaxed lg:w-3/4">
                                    {description}
                                </p>
                            )
                        )}

                        {/* Actions */}
                        {!loading && (primaryAction || secondaryAction) && (
                            <div className="flex flex-col sm:flex-row gap-4 pt-6">
                                {primaryAction &&
                                    (primaryAction.href ? (
                                        primaryAction.external ? (
                                            <a
                                                href={primaryAction.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center justify-center gap-2 bg-brand-dark hover:bg-white hover:text-brand-dark text-white px-4 py-3 uppercase tracking-wide transition-colors duration-200 font-medium text-sm border border-brand-dark hover:cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-dark active:scale-[0.97]"
                                            >
                                                {primaryAction.icon && (
                                                    <span>
                                                        {primaryAction.icon}
                                                    </span>
                                                )}
                                                <span>
                                                    {primaryAction.label}
                                                </span>
                                            </a>
                                        ) : (
                                            <Link
                                                href={primaryAction.href}
                                                onClick={primaryAction.onClick}
                                                className="inline-flex items-center justify-center gap-2 bg-brand-dark hover:bg-white hover:text-brand-dark text-white px-4 py-3 uppercase tracking-wide transition-colors duration-200 font-medium text-sm border border-brand-dark hover:cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-dark active:scale-[0.97]"
                                            >
                                                {primaryAction.icon && (
                                                    <span>
                                                        {primaryAction.icon}
                                                    </span>
                                                )}
                                                <span>
                                                    {primaryAction.label}
                                                </span>
                                            </Link>
                                        )
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={primaryAction.onClick}
                                            className="inline-flex items-center justify-center gap-2 bg-brand-dark hover:bg-white hover:text-brand-dark text-white px-4 py-3 uppercase tracking-wide transition-colors duration-200 font-medium text-sm border border-brand-dark hover:cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-dark active:scale-[0.97]"
                                        >
                                            {primaryAction.icon && (
                                                <span>
                                                    {primaryAction.icon}
                                                </span>
                                            )}
                                            <span>{primaryAction.label}</span>
                                        </button>
                                    ))}
                                {secondaryAction && (
                                    <button
                                        type="button"
                                        onClick={secondaryAction.onClick}
                                        className="text-sm bg-white/10 backdrop-blur-sm border border-white/40 hover:border-white text-white font-medium uppercase tracking-wide px-4 py-3 transition-colors duration-200 flex items-center justify-center space-x-2 hover:bg-white/20 hover:cursor-pointer cursor-pointer active:scale-[0.97]"
                                    >
                                        {secondaryAction.icon && (
                                            <span>{secondaryAction.icon}</span>
                                        )}
                                        <span>{secondaryAction.label}</span>
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Custom children content */}
                        {!loading && children}
                    </div>

                    {/* Optional Product Image */}
                    {showProductImage && productImage && !loading && (
                        <div className="relative">
                            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 h-80 lg:h-96 flex items-center justify-center group relative overflow-hidden">
                                {/* Dynamic background pattern */}
                                <div className="absolute inset-0 opacity-20">
                                    <div
                                        className="absolute inset-0 animate-pulse"
                                        style={{
                                            backgroundImage: `radial-gradient(circle at 3px 3px, rgba(249, 115, 22, 0.4) 1px, transparent 0)`,
                                            backgroundSize: '25px 25px',
                                        }}
                                    ></div>
                                </div>

                                {/* Loading state for product image - subtle blurred placeholder */}
                                {!prodImgLoaded && !prodImgError && (
                                    <div className="absolute inset-0 flex items-center justify-center z-10">
                                        <div
                                            className="w-40 h-32 bg-white/6 backdrop-blur-sm rounded-md motion-safe:animate-pulse motion-reduce:opacity-80"
                                            aria-hidden="true"
                                        />
                                    </div>
                                )}

                                {/* Error state for product image */}
                                {prodImgError ? (
                                    <div className="flex flex-col items-center justify-center h-full w-full z-10">
                                        <ImageOff className="w-16 h-16 text-white/50 mb-4" />
                                        <p className="text-white/70 text-center">
                                            Image unavailable
                                        </p>
                                    </div>
                                ) : (
                                    // Responsive product image with error handling
                                    <div
                                        className={`w-full h-full p-8 flex items-center justify-center`}
                                    >
                                        <AppImage
                                            src={
                                                resolvedProdSrc || productImage
                                            }
                                            alt={productImageAlt}
                                            width={500}
                                            height={400}
                                            className={`object-contain w-full h-full transition-transform duration-700 relative z-10 drop-shadow-2xl ${
                                                onProductImageClick
                                                    ? 'cursor-pointer'
                                                    : ''
                                            } ${prodImgLoaded ? 'opacity-100' : 'opacity-0'}`}
                                            onClick={onProductImageClick}
                                            priority
                                            fetchPriority="high"
                                            sizes="(max-width: 768px) 100vw, 50vw"
                                            // NOTE: prodSrcSet entries are CDN keys too, so they
                                            // need toCdnUrl() for the same reason as bgSrcSet.
                                            // This block is currently unreachable — no caller sets
                                            // showProductImage — so it is left as-is rather than
                                            // shipping an unverifiable change. Fix if that changes.
                                            srcSet={
                                                prodSrcSet
                                                    ? formatSrcSet(
                                                          prodSrcSet,
                                                          (url) =>
                                                              encodeURI(url),
                                                      )
                                                    : undefined
                                            }
                                            onLoad={() =>
                                                setProdImgLoaded(true)
                                            }
                                            onError={() => {
                                                logEvent({
                                                    level: 'warn',
                                                    service: 'page_header',
                                                    event: 'product_image_failed',
                                                    productImage:
                                                        resolvedProdSrc ||
                                                        productImage ||
                                                        null,
                                                });
                                                setProdImgError(true);
                                                setProdImgLoaded(true);
                                            }}
                                            placeholder="blur"
                                            blurDataURL={
                                                prodBlurDataURL ||
                                                getBlurDataURL(16, 12)
                                            }
                                        />
                                    </div>
                                )}

                                {/* Enhanced click to zoom indicator - unchanged */}
                                {onProductImageClick && !prodImgError && (
                                    <div className="absolute top-6 right-6 bg-black/70 text-white px-3 py-2 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center space-x-1">
                                        <svg
                                            className="w-4 h-4"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            aria-hidden="true"
                                        >
                                            <title>Zoom</title>
                                            <path
                                                fillRule="evenodd"
                                                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <span>Click to enlarge</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showScrollButton ? (
                <button
                    type="button"
                    className="block md:hidden absolute bottom-2 md:bottom-8 left-1/2 transform -translate-x-1/2 text-white hover:text-gray-200 focus:outline-none z-20"
                    aria-label="Scroll to next section"
                    onClick={(e) => {
                        e.preventDefault();
                        window.scrollBy({
                            top: window.innerHeight - 80,
                            left: 0,
                            behavior: 'smooth',
                        });
                    }}
                >
                    <DownArrow />
                </button>
            ) : null}
        </section>
    );
};

export default PageHeader;
